import React, { useContext, useState, useEffect, useMemo } from 'react';
import { AppContext } from '../context/AppContext';
import MeterCard from '../components/dashboard/MeterCard';
import StopModal from '../components/dashboard/StopModal';
import { Meter } from '../types';
import { ChartBarSquareIcon } from '@heroicons/react/24/solid';
import { MagnifyingGlassIcon, FunnelIcon, Squares2X2Icon, ListBulletIcon } from '@heroicons/react/24/outline';

const Dashboard: React.FC = () => {
  const { meters, stopRecords, startStop, endStop } = useContext(AppContext);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMeter, setSelectedMeter] = useState<Meter | null>(null);
  const [isStarting, setIsStarting] = useState(true);
  const [activeStopRecordId, setActiveStopRecordId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterArea, setFilterArea] = useState('');

  const activeStops = stopRecords.filter(r => !r.endTime);

  useEffect(() => {
    return () => {
      setError(null);
      setLoading(false);
    };
  }, []);

  const uniqueAreas = useMemo(() => Array.from(new Set(meters.map(m => m.area))), [meters]);

  const filteredMeters = useMemo(() => {
    return meters.filter(meter => {
      const term = searchTerm.toLowerCase().trim();
      const matchesSearch = !term || meter.name.toLowerCase().includes(term);
      const matchesArea = !filterArea || meter.area === filterArea;
      return matchesSearch && matchesArea;
    });
  }, [meters, searchTerm, filterArea]);

  const handleStartStop = async (meterId: string) => {
    try {
      setError(null);
      setLoading(true);

      const meter = meters.find(m => m.id === meterId);
      if (!meter) {
        throw new Error('Medidor no encontrado');
      }

      setSelectedMeter(meter);
      setIsStarting(true);
      setIsModalOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
      console.error('Error in handleStartStop:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEndStop = async (recordId: string) => {
    try {
      setError(null);
      setLoading(true);

      const record = stopRecords.find(r => r.id === recordId);
      if (!record) {
        throw new Error('Registro de parada no encontrado');
      }

      const meter = meters.find(m => m.id === record.meterId);
      if (!meter) {
        throw new Error('Medidor no encontrado');
      }

      setSelectedMeter(meter);
      setActiveStopRecordId(recordId);
      setIsStarting(false);
      setIsModalOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
      console.error('Error in handleEndStop:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMeter(null);
    setActiveStopRecordId(null);
    setError(null);
  };

  const handleSubmitModal = async (data: any) => {
    try {
      setError(null);
      setLoading(true);

      if (isStarting && selectedMeter) {
        await startStop({
          meterId: selectedMeter.id,
          reason: data.reason,
          supervisorName: data.supervisorName,
          startValue: data.value,
          startPhoto: data.photo,
          isManualStart: data.isManual,
        }, data.photoFile);
      } else if (!isStarting && activeStopRecordId) {
        await endStop({
          recordId: activeStopRecordId,
          endValue: data.value,
          endPhoto: data.photo,
          endPhotoFile: data.photoFile,
          isManualEnd: data.isManual
        });
      }

      handleCloseModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al procesar la operación');
      console.error('Error in handleSubmitModal:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-in fade-in duration-500 w-full space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Dashboard de Medidores</h1>
          <p className="text-gray-500 mt-1">Monitorea el estado actual de los medidores y sus paradas.</p>
        </div>

        <div className="flex items-center gap-4">
          {loading && (
            <div className="flex items-center text-brand-primary">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-primary mr-2"></div>
              <span className="text-sm font-medium">Procesando...</span>
            </div>
          )}

          <a
            href="https://grafana-process.bk-ingenieria.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-5 py-2.5 bg-[#F4B400] hover:bg-[#DBA200] text-gray-900 font-bold rounded-xl shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5 active:scale-95"
            title="Abrir Dashboard de Grafana"
          >
            <ChartBarSquareIcon className="h-6 w-6 text-gray-900" />
            Abrir Grafana
          </a>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 ml-1">Buscar Medidor</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Escribe el nombre del equipo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full border-gray-200 rounded-xl focus:ring-brand-primary focus:border-brand-primary p-2.5 bg-gray-50 hover:bg-white transition-colors border"
            />
          </div>
        </div>
        <div className="sm:w-64">
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 ml-1">Filtrar por Área</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FunnelIcon className="h-5 w-5 text-gray-400" />
            </div>
            <select
              value={filterArea}
              onChange={(e) => setFilterArea(e.target.value)}
              className="pl-10 w-full border-gray-200 rounded-xl focus:ring-brand-primary focus:border-brand-primary p-2.5 bg-gray-50 hover:bg-white transition-colors border appearance-none"
            >
              <option value="">Todas las áreas</option>
              {uniqueAreas.map(area => (
                <option key={area} value={area}>{area}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex flex-col sm:w-32 justify-end">
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 ml-1 text-center sm:text-left">Vista</label>
          <div className="flex bg-gray-100 rounded-xl p-1 shadow-inner self-center sm:self-auto w-fit sm:w-full">
            <button
              onClick={() => setViewMode('grid')}
              className={`flex-1 flex justify-center items-center p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white shadow-sm text-brand-primary' : 'text-gray-500 hover:text-gray-700'}`}
              title="Vista Galería (Tarjetas)"
            >
              <Squares2X2Icon className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex-1 flex justify-center items-center p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-brand-primary' : 'text-gray-500 hover:text-gray-700'}`}
              title="Vista Listado (Tabla)"
            >
              <ListBulletIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-r-lg shadow-sm flex justify-between items-center animate-in slide-in-from-top-2">
          <div className="flex items-center gap-2">
            <strong className="font-bold">Error: </strong>
            <span>{error}</span>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-400 hover:text-red-600 transition-colors p-1"
          >
            ✕
          </button>
        </div>
      )}

      {meters.length === 0 ? (
        <div className="text-center py-16 px-4 bg-white rounded-2xl border border-dashed border-gray-300 shadow-sm">
          <p className="text-gray-500 font-medium text-lg">No hay medidores registrados</p>
          <p className="text-gray-400 text-sm mt-1">Ve a la sección Administrar Medidores para agregar tu primer equipo.</p>
        </div>
      ) : filteredMeters.length === 0 ? (
        <div className="text-center py-12 px-4 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
          <p className="text-gray-500 font-medium text-lg">No se encontraron medidores</p>
          <p className="text-gray-400 text-sm mt-1">Intenta con otros términos de búsqueda o área.</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMeters.map(meter => (
            <MeterCard
              key={meter.id}
              meter={meter}
              activeStop={activeStops.find(s => s.meterId === meter.id)}
              onStartStop={handleStartStop}
              onEndStop={handleEndStop}
              disabled={loading}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Estado</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Equipo / Nombre</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Área o Sector</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Suministro</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Detalle Parada Activa</th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Acción</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filteredMeters.map(meter => {
                  const activeStop = activeStops.find(s => s.meterId === meter.id);
                  const isStopped = !!activeStop;
                  return (
                    <tr key={meter.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${isStopped
                          ? 'bg-red-50 text-red-600 border border-red-100'
                          : 'bg-green-50 text-green-600 border border-green-100'
                        }`}>
                          {isStopped ? 'Detenido' : 'En Operación'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-gray-900">{meter.name}</div>
                        <div className="text-xs text-gray-400 font-mono mt-0.5">ID: {meter.id.substring(0, 8)}...</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-700">{meter.area}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{meter.type}</div>
                        <div className="text-xs text-gray-500 font-semibold">{meter.unit}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isStopped ? (
                          <div>
                            <div className="text-sm font-bold text-red-600 truncate max-w-xs">{activeStop.reason}</div>
                            <div className="text-xs text-gray-500 font-medium mt-0.5">
                              Resp: {activeStop.supervisorName || 'N/A'} • {new Date(activeStop.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400 font-medium">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        {isStopped ? (
                          <button
                            onClick={() => handleEndStop(activeStop.id)}
                            disabled={loading}
                            className={`px-3 py-1.5 text-xs bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg shadow-sm transition-colors ${loading ? 'opacity-50 cursor-not-allowed' : 'active:scale-95 transform'}`}
                          >
                            Fin de Parada
                          </button>
                        ) : (
                          <button
                            onClick={() => handleStartStop(meter.id)}
                            disabled={loading}
                            className={`px-3 py-1.5 text-xs bg-gray-100 hover:bg-[#E2B53F] text-gray-700 hover:text-gray-900 border border-gray-200 hover:border-[#E2B53F] font-bold rounded-lg shadow-sm transition-colors ${loading ? 'opacity-50 cursor-not-allowed' : 'active:scale-95 transform'}`}
                          >
                            Inicio de Parada
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isModalOpen && (
        <StopModal
          meter={selectedMeter}
          isStarting={isStarting}
          activeStop={activeStopRecordId ? stopRecords.find(r => r.id === activeStopRecordId) : undefined}
          onClose={handleCloseModal}
          onSubmit={handleSubmitModal}
          loading={loading}
        />
      )}
    </div>
  );
};

export default Dashboard;